import { useState, useCallback, useEffect, useMemo } from "react";
import {
  Plus,
  Bot,
  Cloud,
  Server,
  Zap,
  Brain,
  MonitorSmartphone,
  Eye,
  EyeOff,
  Loader2,
  AlertTriangle,
  Trash2,
  Search,
  Power,
  Ban,
  Pencil,
  Activity,
  Send,
} from "lucide-react";
import {
  Modal,
  FormField,
  FormInput,
  FormSelect,
  ModalFooterButtons,
} from "../components/Modal";
import { RBACAction } from "../components/RBACButton";
import { Dropdown } from "../components/Dropdown";
import { TableToolbar } from "../components/TableToolbar";
import { Tip } from "../components/ui/tooltip";
import { useRBACPermission } from "../../hooks/useRBACPermission";
import { showToast } from "../components/ui/custom-toast";
import { getAPI, postAPI, putAPI, deleteAPI } from "../../api/frameworksApi";
import { API_URL } from "../../constants";
import {
  validateMaxLength,
  validateURL,
  validateJSON,
  validateNumberRange,
} from "../../utils/validators";

// ─── Types (mirror backend) ──────────────────────────────────────────────────

type ProviderType =
  | "ANTHROPIC"
  | "OPENAI"
  | "AZURE_OPENAI"
  | "BEDROCK"
  | "GEMINI"
  | "LOCAL_LLM"
  | "GROQ"
  | "NVIDIA_NIM";

interface OrgUnit {
  id: string;
  name: string;
  shortName?: string;
  orgType?: string;
  isActive?: boolean;
}
type FieldType = "PASSWORD" | "STRING" | "URL" | "NUMBER" | "JSON";

interface ProviderField {
  name: string;
  label: string;
  type: FieldType;
  required: boolean;
  placeholder?: string;
  helpText?: string;
  examples?: string[];
}

interface ProviderMeta {
  provider: ProviderType;
  displayName: string;
  fields: ProviderField[];
}

// Provider metadata from the backend only lists chat model examples on the
// `modelName` field. To offer the same chip-selection UX in the Embedding tab,
// we ship a curated per-provider list of embedding foundation model IDs.
const EMBEDDING_MODEL_EXAMPLES: Partial<Record<ProviderType, string[]>> = {
  OPENAI: [
    "text-embedding-3-small",
    "text-embedding-3-large",
    "text-embedding-ada-002",
  ],
  AZURE_OPENAI: [
    "text-embedding-3-small",
    "text-embedding-3-large",
    "text-embedding-ada-002",
  ],
  BEDROCK: [
    "amazon.titan-embed-text-v2:0",
    "amazon.titan-embed-text-v1",
    "cohere.embed-english-v3",
    "cohere.embed-multilingual-v3",
  ],
  GEMINI: ["text-embedding-004", "embedding-001"],
  LOCAL_LLM: ["bge-small-en-v1.5", "bge-large-en-v1.5", "nomic-embed-text"],
  NVIDIA_NIM: ["nvidia/nv-embed-v1", "nvidia/nv-embedqa-e5-v5"],
};

const EMBEDDING_MODEL_PLACEHOLDER: Partial<Record<ProviderType, string>> = {
  OPENAI: "e.g. text-embedding-3-small",
  AZURE_OPENAI: "e.g. text-embedding-3-small",
  BEDROCK: "amazon.titan-embed-text-v2:0",
  GEMINI: "e.g. text-embedding-004",
  LOCAL_LLM: "e.g. bge-small-en-v1.5",
  NVIDIA_NIM: "e.g. nvidia/nv-embed-v1",
};

type SubConfigKind = "chat" | "embedding";

// Common per-sub-config fields returned by the backend (credentials are never
// returned). `id` is the per-model id used for activate/deactivate/test.
interface BaseSubConfig {
  id: string;
  provider: ProviderType;
  modelName: string;
  timeoutSeconds?: number;
  maxRetries?: number;
  isActive: boolean;
  createdAt: string;
  updatedAt?: string;
}

interface ChatSubConfig extends BaseSubConfig {
  maxTokens?: number;
  temperature?: number;
  topP?: number;
}

interface EmbeddingSubConfig extends BaseSubConfig {
  dimensions: number;
  contextWindow?: number;
}

// A "set" — one provider configuration that may carry a chat model, an
// embedding model, or both. `id` here is the SET id used for CRUD.
interface LlmConfigSummary {
  id: string;
  orgId?: string;
  name: string;
  chatConfiguration: ChatSubConfig | null;
  embeddingConfiguration: EmbeddingSubConfig | null;
  createdAt: string;
  updatedAt?: string;
}

interface LlmConfig extends LlmConfigSummary {}

interface ApiErrorEntry {
  code: string;
  title?: string;
  detail: string;
  pointer?: string;
  details?: { providerMessage?: string };
}

interface ParsedApiError {
  banner: string;
  details?: string;
  fieldErrors: Record<string, string>;
}

// ─── Provider icons ───────────────────────────────────────────────────────────

const PROVIDER_COLORS: Record<ProviderType, string> = {
  ANTHROPIC: "text-yellow-600",
  OPENAI: "text-emerald-500",
  AZURE_OPENAI: "text-blue-500",
  BEDROCK: "text-orange-500",
  GEMINI: "text-purple-500",
  LOCAL_LLM: "text-cyan-500",
  GROQ: "text-pink-500",
  NVIDIA_NIM: "text-green-500",
};

function ProviderIcon({
  provider,
  size = 18,
}: {
  provider: ProviderType;
  size?: number;
}) {
  const cls = `flex-shrink-0 ${PROVIDER_COLORS[provider] ?? "text-[#64748b]"}`;
  const props = { size, className: cls };
  switch (provider) {
    case "ANTHROPIC":
      return <Brain {...props} />;
    case "OPENAI":
      return <Brain {...props} />;
    case "AZURE_OPENAI":
      return <Cloud {...props} />;
    case "BEDROCK":
      return <Server {...props} />;
    case "GEMINI":
      return <Zap {...props} />;
    case "LOCAL_LLM":
      return <MonitorSmartphone {...props} />;
    case "GROQ":
      return <Zap {...props} />;
    case "NVIDIA_NIM":
      return <Zap {...props} />;
    default:
      return <Bot {...props} />;
  }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

// The list endpoint returns each config in a flat shape:
//   { id, orgId, name, provider, modelType: "CHAT"|"EMBEDDING", modelName, isActive, createdAt, ... }
// while the rest of this page (cards, buildSubTarget, edit form) reads from the
// nested `chatConfiguration` / `embeddingConfiguration` sub-configs. If the
// nested keys are missing, promote the top-level fields into whichever
// sub-config the `modelType` discriminator points to. The synthetic sub-config
// reuses the parent id — in the flat contract one config == one model, so
// that id is also the per-model id used for activate/deactivate/test.
function normalizeConfig(raw: any): LlmConfig {
  if (!raw || typeof raw !== "object") return raw as LlmConfig;
  if (
    raw.chatConfiguration !== undefined ||
    raw.embeddingConfiguration !== undefined
  ) {
    return raw as LlmConfig;
  }
  const sub: any = {
    id: raw.id,
    provider: raw.provider,
    modelName: raw.modelName,
    isActive: !!raw.isActive,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
    timeoutSeconds: raw.timeoutSeconds,
    maxRetries: raw.maxRetries,
    maxTokens: raw.maxTokens,
    temperature: raw.temperature,
    topP: raw.topP,
    dimensions: raw.dimensions,
    contextWindow: raw.contextWindow,
  };
  const isEmbedding = String(raw.modelType ?? "").toUpperCase() === "EMBEDDING";
  return {
    id: raw.id,
    orgId: raw.orgId,
    name: raw.name,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
    chatConfiguration: isEmbedding ? null : (sub as ChatSubConfig),
    embeddingConfiguration: isEmbedding ? (sub as EmbeddingSubConfig) : null,
  };
}

// Parse pointer like "/credentials/apiKey" → "apiKey", or "/modelName" → "modelName"
function pointerToFieldName(pointer?: string): string | undefined {
  if (!pointer) return undefined;
  const parts = pointer.split("/").filter(Boolean);
  return parts.length ? parts[parts.length - 1] : undefined;
}

// BE marks identifier-style credentials (access keys, client IDs) as PASSWORD type
// for safety, but those aren't truly secret — masking them just makes them harder
// to verify on paste. Only mask fields whose name signals a real secret.
function isTrueSecretField(name: string): boolean {
  return /(secret|password|token|private|apikey)/i.test(name);
}

// Friendlier wording per the spec's error-code → user-message table.
// Falls back to whatever the BE put in `detail` if we don't have a custom mapping.
const ERROR_CODE_MESSAGES: Record<string, string> = {
  "LLM-0001-E": "Invalid credentials. Check your keys/tokens and try again.",
  "LLM-0002-E": "Credentials are valid but missing required permissions.",
  "LLM-0003-E": "Model not found or not available in this region/server.",
  "LLM-0004-E": "Cannot reach the LLM endpoint. Check the URL and network.",
  "LLM-0005-E": "No active LLM is configured.",
  "LLM-0006-E": "Invalid configuration — please check the highlighted fields.",
  "LLM-0010-E": "Configuration not found.",
  "LLM-0011-E": "Provider cannot be changed after creation.",
  "LLM-0012-E": "Cannot delete the active LLM. Activate another first.",
  "LLM-0099-E": "Unexpected error contacting the provider.",
};

// Map an axios error to banner / field errors / expandable details per the spec.
function parseApiError(err: any, provider?: ProviderMeta): ParsedApiError {
  const fieldErrors: Record<string, string> = {};
  const errors: ApiErrorEntry[] = err?.response?.data?.errors ?? [];

  if (errors.length === 0) {
    return {
      banner:
        err?.message === "OFFLINE"
          ? "You appear to be offline. Check your connection and try again."
          : (err?.message ?? "Something went wrong. Please try again."),
      fieldErrors,
    };
  }

  let banner = "";
  let details: string | undefined;

  for (const e of errors) {
    if (e.details?.providerMessage)
      details = details ?? e.details.providerMessage;

    // LLM-0005-E carries an org-specific message from the backend — surface it verbatim.
    if (e.code === "LLM-0005-E") {
      banner = banner || e.detail || ERROR_CODE_MESSAGES["LLM-0005-E"];
      continue;
    }

    if (e.code === "LLM-0006-E" || e.code === "SYS-0002-E") {
      const f = pointerToFieldName(e.pointer);
      if (f) {
        fieldErrors[f] = e.detail;
        continue;
      }
    }

    if (e.code === "LLM-0003-E") {
      fieldErrors.modelName = fieldErrors.modelName ?? e.detail;
    }

    if (e.code === "LLM-0001-E" && provider) {
      // Highlight all credential (non-modelName) fields.
      for (const f of provider.fields) {
        if (f.name !== "modelName") {
          fieldErrors[f.name] = fieldErrors[f.name] ?? "Check this credential.";
        }
      }
    }

    if (e.code === "LLM-0004-E" && provider) {
      const urlField = provider.fields.find(
        (f) => f.name === "baseUrl" || f.name === "endpoint",
      );
      if (urlField) {
        fieldErrors[urlField.name] = fieldErrors[urlField.name] ?? e.detail;
      }
    }

    if (!banner) {
      banner = ERROR_CODE_MESSAGES[e.code] ?? e.detail;
    }
  }

  if (!banner && Object.keys(fieldErrors).length > 0) {
    banner = "Please fix the highlighted fields and try again.";
  }

  return { banner, details, fieldErrors };
}

// ─── ProviderFieldInput ───────────────────────────────────────────────────────

interface ProviderFieldInputProps {
  field: ProviderField;
  value: string;
  onChange: (v: string) => void;
  showPassword: boolean;
  onTogglePassword: () => void;
  error?: string;
  isEdit: boolean;
}

function ProviderFieldInput({
  field,
  value,
  onChange,
  showPassword,
  onTogglePassword,
  error,
  isEdit,
}: ProviderFieldInputProps) {
  // Only mask fields that are *actually* secret. AWS Access Key / Client ID etc
  // come back as PASSWORD from the BE but they're identifiers, not secrets.
  const isMasked = field.type === "PASSWORD" && isTrueSecretField(field.name);
  const editPlaceholder =
    isEdit && !value ? "•••••••• (unchanged)" : field.placeholder;
  const fieldName = `llm-cred-${field.name}`;

  const [jsonBlurError, setJsonBlurError] = useState<string | undefined>();
  const handleJsonBlur = () => {
    if (field.type === "JSON" && value.trim()) {
      try {
        JSON.parse(value);
        setJsonBlurError(undefined);
      } catch {
        setJsonBlurError("Must be valid JSON.");
      }
    } else {
      setJsonBlurError(undefined);
    }
  };
  const displayError = error ?? jsonBlurError;

  return (
    <FormField
      label={field.label + (!field.required ? "" : "")}
      required={field.required}
    >
      <div className="space-y-1.5">
        {field.type === "JSON" ? (
          <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onBlur={handleJsonBlur}
            placeholder={field.placeholder ?? '{ "X-Header": "value" }'}
            rows={4}
            spellCheck={false}
            name={fieldName}
            autoComplete="off"
            className="w-full text-sm bg-[#f5f7fb] dark:bg-white/[0.04] rounded-lg px-3 py-2 outline-none border border-[#e2e8f0] dark:border-white/[0.07] focus:border-indigo-500 dark:focus:border-indigo-500 transition-all placeholder:text-[#94a3b8] dark:placeholder:text-white/25 resize-none leading-relaxed text-[#0f172a] dark:text-white font-mono"
          />
        ) : isMasked ? (
          <div className="relative">
            <FormInput
              type={showPassword ? "text" : "password"}
              value={value}
              onChange={onChange}
              placeholder={editPlaceholder}
              name={fieldName}
              autoComplete="new-password"
            />
            <button
              type="button"
              onClick={onTogglePassword}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[#94a3b8] hover:text-[#475569] dark:hover:text-white/60 transition-colors"
            >
              {showPassword ? (
                <EyeOff className="size-4" />
              ) : (
                <Eye className="size-4" />
              )}
            </button>
          </div>
        ) : (
          <FormInput
            type={field.type === "NUMBER" ? "number" : "text"}
            value={value}
            onChange={onChange}
            placeholder={isEdit && !value ? "(unchanged)" : field.placeholder}
            name={fieldName}
            autoComplete="off"
          />
        )}

        {!field.required && (
          <p className="text-xs italic text-[#94a3b8] dark:text-white/30">
            (optional)
          </p>
        )}

        {field.helpText && <p className="text-xs d">{field.helpText}</p>}

        {field.examples && field.examples.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {field.examples.map((ex) => (
              <button
                key={ex}
                type="button"
                onClick={() => onChange(ex)}
                className="text-xs px-2 py-0.5 rounded-md bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-500/20 transition-colors font-mono"
              >
                {ex}
              </button>
            ))}
          </div>
        )}

        {displayError && <p className="text-xs text-red-500">{displayError}</p>}
      </div>
    </FormField>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const LS_ORG_KEY = "llm-configurations:org";

export function LlmConfigurations() {
  // ── Org picker state ──────────────────────────────────────────────────────
  const [orgs, setOrgs] = useState<OrgUnit[]>([]);
  const [orgsLoading, setOrgsLoading] = useState(true);
  const [orgsError, setOrgsError] = useState<string | null>(null);
  const [selectedOrgId, setSelectedOrgId] = useState<string>(
    () => localStorage.getItem(LS_ORG_KEY) ?? "",
  );

  // ── Data state ────────────────────────────────────────────────────────────
  const [configs, setConfigs] = useState<LlmConfig[]>([]);
  const [providers, setProviders] = useState<ProviderMeta[]>([]);
  // /active endpoint is documented as unchanged and still returns a per-model
  // view (the currently-active chat model that AI features route to). Kept as
  // a loose shape since only display fields are read.
  const [activeConfig, setActiveConfig] = useState<
    { id: string; name?: string; modelName?: string } | null | undefined
  >(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // ── Card search ───────────────────────────────────────────────────────────
  const [llmSearch, setLlmSearch] = useState("");
  // Which model type the list is scoped to. The GET /llm-configurations endpoint
  // accepts a `modelType=CHAT|EMBEDDING` filter and returns only that kind, so
  // the tab controls the fetch — Chat and Embedding cards never mix.
  const [listModelType, setListModelType] = useState<"CHAT" | "EMBEDDING">(
    "CHAT",
  );

  // ── Form modal ────────────────────────────────────────────────────────────
  const [showForm, setShowForm] = useState(false);
  const [editConfig, setEditConfig] = useState<LlmConfig | null>(null);
  const [editLoadingId, setEditLoadingId] = useState<string | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<ProviderMeta | null>(
    null,
  );
  const [formOrgId, setFormOrgId] = useState<string>("");
  // Form values are keyed with a `chat_` / `embedding_` prefix for per-tab
  // fields (e.g. `chat_modelName`, `embedding_dimensions`), no prefix for
  // shared fields (`name`, credential fields).
  const [formValues, setFormValues] = useState<Record<string, string>>({});
  const [showPw, setShowPw] = useState<Record<string, boolean>>({});
  // Each modal instance edits exactly one model type. `formModelType` is set
  // from the list tab on Add and from the target config on Edit, and locks
  // which set of fields (Chat or Embedding) the modal renders + saves.
  const [formModelType, setFormModelType] = useState<"CHAT" | "EMBEDDING">(
    "CHAT",
  );
  // Kept in sync with `formModelType` — the save/validation branches gate on
  // these, so flipping them here means we don't need to touch that logic.
  const [chatEnabled, setChatEnabled] = useState(true);
  const [embeddingEnabled, setEmbeddingEnabled] = useState(false);

  const [formSaving, setFormSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [formErrorDetails, setFormErrorDetails] = useState<string | null>(null);
  const [formFieldErrors, setFormFieldErrors] = useState<
    Record<string, string>
  >({});

  // Activate/deactivate/test target the per-model id inside a set. This
  // carries both the parent set (for wording + refresh) and the specific
  // sub-model being acted on.
  interface SubTarget {
    setId: string;
    setName: string;
    modelId: string;
    modelName: string;
    kind: SubConfigKind;
    isActive: boolean;
  }

  // ── Activate modal ────────────────────────────────────────────────────────
  const [showActivate, setShowActivate] = useState(false);
  const [activateTarget, setActivateTarget] = useState<SubTarget | null>(null);
  const [activating, setActivating] = useState(false);
  const [activateError, setActivateError] = useState<string | null>(null);
  const [activateDetails, setActivateDetails] = useState<string | null>(null);

  // ── Deactivate modal ──────────────────────────────────────────────────────
  const [showDeactivate, setShowDeactivate] = useState(false);
  const [deactivateTarget, setDeactivateTarget] = useState<SubTarget | null>(
    null,
  );
  const [deactivating, setDeactivating] = useState(false);
  const [deactivateError, setDeactivateError] = useState<string | null>(null);

  // ── Delete modal ──────────────────────────────────────────────────────────
  const [showDelete, setShowDelete] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<LlmConfigSummary | null>(
    null,
  );
  const [deleting, setDeleting] = useState(false);

  // ── Test connection ───────────────────────────────────────────────────────
  const [testingId, setTestingId] = useState<string | null>(null);

  // ── Chat tester ───────────────────────────────────────────────────────────
  interface ChatResponse {
    content: string;
    modelUsed: string;
    inputTokens: number;
    outputTokens: number;
    latencyMs: number;
  }
  const CHAT_PROMPT_MAX = 5000;
  const [showChat, setShowChat] = useState(false);
  const [chatPrompt, setChatPrompt] = useState("");
  const [chatSending, setChatSending] = useState(false);
  const [chatResponse, setChatResponse] = useState<ChatResponse | null>(null);
  const [chatError, setChatError] = useState<string | null>(null);
  const [chatErrorDetails, setChatErrorDetails] = useState<string | null>(null);

  // ── Fetch accessible orgs once ────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setOrgsLoading(true);
      setOrgsError(null);
      try {
        // Scope to the organizations the current user can access for LLM
        // configuration (module=llm_configuration, action=view) — the same RBAC
        // pattern the Roles → Group Mappings screen uses — instead of loading
        // every org unit via /org-units?flat=true. This hides unauthorized orgs
        // and keeps every downstream request scoped to a permitted org.
        const res = await getAPI(
          `${API_URL.ACCESSIBLE_ORGS}?module=llm_configuration&action=view&org_type=ORGANIZATION`,
        );
        if (cancelled) return;
        const list: OrgUnit[] = res?.data?.organizations ?? [];
        setOrgs(list);
        setSelectedOrgId(list[0]?.id ?? "");
      } catch (e: any) {
        if (cancelled) return;
        setOrgsError(
          e?.message === "OFFLINE"
            ? "You appear to be offline."
            : "Failed to load organizations.",
        );
      } finally {
        if (!cancelled) setOrgsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Persist org selection so it survives navigation.
  useEffect(() => {
    if (selectedOrgId) localStorage.setItem(LS_ORG_KEY, selectedOrgId);
  }, [selectedOrgId]);

  const selectedOrg = useMemo(
    () => orgs.find((o) => o.id === selectedOrgId),
    [orgs, selectedOrgId],
  );

  // ── Fetch providers once (provider catalog is org-independent) ────────────
  useEffect(() => {
    (async () => {
      try {
        const provRes = await getAPI(`${API_URL.LLM_CONFIGS}/providers`);
        const allowedProviders = new Set(["ANTHROPIC", "OPENAI", "AZURE_OPENAI", "BEDROCK", "GEMINI", "LOCAL_LLM", "GROQ", "NVIDIA_NIM"]);
        setProviders(
          (provRes?.data ?? []).filter((p: ProviderMeta) =>
            allowedProviders.has(p.provider),
          ),
        );
      } catch {
        // Surfaced via banner in the form when providers list is empty.
      }
    })();
  }, []);

  // If Edit was opened before the provider catalog finished loading, openEdit
  // couldn't resolve `selectedProvider` from `providers`. Re-bind it once the
  // catalog arrives so the credential fields and Embedding foundation-model
  // chips render.
  useEffect(() => {
    if (!editConfig || selectedProvider || providers.length === 0) return;
    const providerValue =
      editConfig.chatConfiguration?.provider ??
      editConfig.embeddingConfiguration?.provider;
    if (!providerValue) return;
    const meta = providers.find((p) => p.provider === providerValue);
    if (meta) setSelectedProvider(meta);
  }, [providers, editConfig, selectedProvider]);

  // ── Fetch configs + active for the selected org ───────────────────────────
  const fetchData = useCallback(async () => {
    if (!selectedOrgId) {
      setConfigs([]);
      setActiveConfig(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const q = `?orgId=${encodeURIComponent(selectedOrgId)}`;
      const listQ = `${q}&modelType=${listModelType}`;
      const [listRes, activeRes] = await Promise.all([
        getAPI(`${API_URL.LLM_CONFIGS}${listQ}`),
        getAPI(`${API_URL.LLM_CONFIGS}/active${q}`),
      ]);
      const rawSummaries: any[] = listRes?.data ?? [];
      const summaries = rawSummaries.map(normalizeConfig);
      // Fetch full details for each config in parallel to get maxTokens, temperature, etc.
      const fullConfigs = await Promise.all(
        summaries.map((s) =>
          getAPI(`${API_URL.LLM_CONFIGS}/${s.id}`)
            .then((r) => normalizeConfig(r?.data ?? s))
            .catch(() => s),
        ),
      );
      setConfigs(fullConfigs);
      setActiveConfig(activeRes?.data ?? null);
    } catch (e: any) {
      setError(
        e?.message === "OFFLINE"
          ? "You appear to be offline."
          : "Failed to load LLM configurations.",
      );
    } finally {
      setLoading(false);
    }
  }, [selectedOrgId, listModelType]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const refreshList = useCallback(async () => {
    if (!selectedOrgId) return;
    try {
      const q = `?orgId=${encodeURIComponent(selectedOrgId)}`;
      const listQ = `${q}&modelType=${listModelType}`;
      const [listRes, activeRes] = await Promise.all([
        getAPI(`${API_URL.LLM_CONFIGS}${listQ}`),
        getAPI(`${API_URL.LLM_CONFIGS}/active${q}`),
      ]);
      const rawSummaries: any[] = listRes?.data ?? [];
      const summaries = rawSummaries.map(normalizeConfig);
      const fullConfigs = await Promise.all(
        summaries.map((s) =>
          getAPI(`${API_URL.LLM_CONFIGS}/${s.id}`)
            .then((r) => normalizeConfig(r?.data ?? s))
            .catch(() => s),
        ),
      );
      setConfigs(fullConfigs);
      setActiveConfig(activeRes?.data ?? null);
    } catch {
      // surfaced by the next page fetch / toast on action
    }
  }, [selectedOrgId, listModelType]);

  // ── Open Add ──────────────────────────────────────────────────────────────
  const openAdd = () => {
    setEditConfig(null);
    setSelectedProvider(null);
    setFormOrgId(selectedOrgId || orgs[0]?.id || "");
    setFormValues({
      // Chat defaults
      chat_maxTokens: "4096",
      chat_temperature: "0.7",
      chat_topP: "1.0",
      chat_timeoutSeconds: "60",
      chat_maxRetries: "3",
      // Embedding defaults
      embedding_timeoutSeconds: "60",
      embedding_maxRetries: "3",
    });
    // Add mode inherits the list tab — the modal is single-purpose.
    const openingEmbedding = listModelType === "EMBEDDING";
    setFormModelType(openingEmbedding ? "EMBEDDING" : "CHAT");
    setChatEnabled(!openingEmbedding);
    setEmbeddingEnabled(openingEmbedding);
    setShowPw({});
    setFormError(null);
    setFormErrorDetails(null);
    setFormFieldErrors({});
    setShowForm(true);
  };

  // ── Open Edit (pre-fetch full config) ─────────────────────────────────────
  const openEdit = async (row: LlmConfigSummary) => {
    setEditLoadingId(row.id);
    try {
      const res = await getAPI(`${API_URL.LLM_CONFIGS}/${row.id}`);
      const cfg: LlmConfig = normalizeConfig(res?.data);
      // Provider is shared in the UI; both sub-configs use it. Fall back to
      // whichever one exists on the fetched set.
      const providerValue =
        cfg.chatConfiguration?.provider ??
        cfg.embeddingConfiguration?.provider;
      const provMeta = providerValue
        ? (providers.find((p) => p.provider === providerValue) ?? null)
        : null;
      setEditConfig(cfg);
      setSelectedProvider(provMeta);
      const chat = cfg.chatConfiguration;
      const emb = cfg.embeddingConfiguration;
      setFormValues({
        name: cfg.name,
        // Chat sub-config
        chat_modelName: chat?.modelName ?? "",
        chat_maxTokens: String(chat?.maxTokens ?? 4096),
        chat_temperature: String(chat?.temperature ?? 0.7),
        chat_topP: String(chat?.topP ?? 1.0),
        chat_timeoutSeconds: String(chat?.timeoutSeconds ?? 60),
        chat_maxRetries: String(chat?.maxRetries ?? 3),
        // Embedding sub-config
        embedding_modelName: emb?.modelName ?? "",
        embedding_dimensions: emb?.dimensions != null ? String(emb.dimensions) : "",
        embedding_contextWindow:
          emb?.contextWindow != null ? String(emb.contextWindow) : "",
        embedding_timeoutSeconds: String(emb?.timeoutSeconds ?? 60),
        embedding_maxRetries: String(emb?.maxRetries ?? 3),
      });
      // A config record is single-typed via the flat modelType contract, so
      // exactly one of chat/emb is populated. Lock the modal to that type.
      const kind: "CHAT" | "EMBEDDING" = chat ? "CHAT" : "EMBEDDING";
      setFormModelType(kind);
      setChatEnabled(kind === "CHAT");
      setEmbeddingEnabled(kind === "EMBEDDING");
      setShowPw({});
      setFormError(null);
      setFormErrorDetails(null);
      setFormFieldErrors({});
      setShowForm(true);
    } catch (e: any) {
      const parsed = parseApiError(e);
      showToast("error", parsed.banner);
    } finally {
      setEditLoadingId(null);
    }
  };

  const setField = (name: string, value: string) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
    if (formFieldErrors[name]) {
      setFormFieldErrors((prev) => {
        const n = { ...prev };
        delete n[name];
        return n;
      });
    }
  };

  const handleProviderChange = (providerValue: string) => {
    const meta = providers.find((p) => p.provider === providerValue) ?? null;
    setSelectedProvider(meta);
    // Provider change clears the model names in each tab (a new provider's
    // model catalog is different) but preserves shared name + numeric knobs.
    setFormValues((prev) => ({
      name: prev.name ?? "",
      chat_modelName: "",
      chat_maxTokens: prev.chat_maxTokens ?? "4096",
      chat_temperature: prev.chat_temperature ?? "0.7",
      chat_topP: prev.chat_topP ?? "1.0",
      chat_timeoutSeconds: prev.chat_timeoutSeconds ?? "60",
      chat_maxRetries: prev.chat_maxRetries ?? "3",
      embedding_modelName: "",
      embedding_dimensions: prev.embedding_dimensions ?? "",
      embedding_contextWindow: prev.embedding_contextWindow ?? "",
      embedding_timeoutSeconds: prev.embedding_timeoutSeconds ?? "60",
      embedding_maxRetries: prev.embedding_maxRetries ?? "3",
    }));
    setFormFieldErrors({});
  };

  // ── Save (create / update) ────────────────────────────────────────────────
  const handleSave = async () => {
    if (!selectedProvider) return;

    setFormError(null);
    setFormErrorDetails(null);

    // Credential fields are everything except modelName (which is a common field).
    // Under the set model, credentials are shared: the same object is duplicated
    // into both chatConfiguration.credentials and embeddingConfiguration.credentials.
    const credFields = selectedProvider.fields.filter(
      (f) => f.name !== "modelName",
    );
    const userFilledAnyCred = credFields.some(
      (f) => !!formValues[f.name]?.trim(),
    );
    // On edit, credentials are never returned by the BE, so we only re-send
    // them (a) on create, or (b) if the user filled at least one cred field.
    const validateCreds = !editConfig || userFilledAnyCred;

    const fieldErrors: Record<string, string> = {};

    // Shared: Name
    const nameValidation = validateMaxLength(
      formValues.name?.trim() ?? "",
      100,
      "Name",
    );
    if (!nameValidation.valid) {
      fieldErrors.name =
        nameValidation.error ||
        "Name is required and must not exceed 100 characters.";
    } else if (!formValues.name?.trim()) {
      fieldErrors.name = "Name is required.";
    }

    // BE (LLM-0016-E) requires at least one sub-config on create; enforce
    // the same on edit too so we never send both blocks as null.
    if (!chatEnabled && !embeddingEnabled) {
      setFormError("Enable at least one of Chat or Embedding.");
      return;
    }

    // Shared numeric parse helper
    const num = (v: string | undefined) => (v?.trim() ? Number(v) : NaN);

    // Per-tab: Chat
    let chatMaxTokens = NaN;
    let chatTemperature = NaN;
    let chatTopP = NaN;
    let chatTimeout = NaN;
    let chatRetries = NaN;
    if (chatEnabled) {
      const chatModelVal = validateMaxLength(
        formValues.chat_modelName?.trim() ?? "",
        255,
        "Chat model name",
      );
      if (!chatModelVal.valid) {
        fieldErrors.chat_modelName =
          chatModelVal.error ||
          "Chat model name is required and must not exceed 255 characters.";
      } else if (!formValues.chat_modelName?.trim()) {
        fieldErrors.chat_modelName = "Chat model name is required.";
      }
      chatMaxTokens = num(formValues.chat_maxTokens);
      chatTemperature = num(formValues.chat_temperature);
      chatTopP = num(formValues.chat_topP);
      chatTimeout = num(formValues.chat_timeoutSeconds);
      chatRetries = num(formValues.chat_maxRetries);
      if (Number.isFinite(chatMaxTokens)) {
        const v = validateNumberRange(chatMaxTokens, 1, 1_000_000, "Max tokens");
        if (!v.valid)
          fieldErrors.chat_maxTokens =
            v.error || "Must be between 1 and 1,000,000.";
      }
      if (Number.isFinite(chatTemperature)) {
        const v = validateNumberRange(chatTemperature, 0, 2, "Temperature");
        if (!v.valid)
          fieldErrors.chat_temperature = v.error || "Must be between 0 and 2.";
      }
      if (Number.isFinite(chatTopP)) {
        const v = validateNumberRange(chatTopP, 0, 1, "Top P");
        if (!v.valid) fieldErrors.chat_topP = v.error || "Must be between 0 and 1.";
      }
      if (Number.isFinite(chatTimeout)) {
        const v = validateNumberRange(chatTimeout, 1, 600, "Timeout");
        if (!v.valid)
          fieldErrors.chat_timeoutSeconds =
            v.error || "Must be between 1 and 600.";
      }
      if (Number.isFinite(chatRetries)) {
        const v = validateNumberRange(chatRetries, 0, 10, "Max retries");
        if (!v.valid)
          fieldErrors.chat_maxRetries = v.error || "Must be between 0 and 10.";
      }
    }

    // Per-tab: Embedding
    let embDimensions = NaN;
    let embContextWindow = NaN;
    let embTimeout = NaN;
    let embRetries = NaN;
    if (embeddingEnabled) {
      const embModelVal = validateMaxLength(
        formValues.embedding_modelName?.trim() ?? "",
        255,
        "Embedding model name",
      );
      if (!embModelVal.valid) {
        fieldErrors.embedding_modelName =
          embModelVal.error ||
          "Embedding model name is required and must not exceed 255 characters.";
      } else if (!formValues.embedding_modelName?.trim()) {
        fieldErrors.embedding_modelName = "Embedding model name is required.";
      }
      embDimensions = num(formValues.embedding_dimensions);
      embContextWindow = num(formValues.embedding_contextWindow);
      embTimeout = num(formValues.embedding_timeoutSeconds);
      embRetries = num(formValues.embedding_maxRetries);
      // Dimensions is required on create (BE enforces > 0) and immutable
      // afterwards. Only validate on create or when the user changes it.
      const existingDims = editConfig?.embeddingConfiguration?.dimensions;
      const isCreatingEmb = !editConfig?.embeddingConfiguration;
      if (isCreatingEmb) {
        if (!formValues.embedding_dimensions?.trim()) {
          fieldErrors.embedding_dimensions =
            "Dimensions is required (positive integer).";
        } else if (!Number.isFinite(embDimensions) || embDimensions <= 0) {
          fieldErrors.embedding_dimensions =
            "Dimensions must be a positive integer.";
        }
      } else if (
        Number.isFinite(embDimensions) &&
        embDimensions !== existingDims
      ) {
        fieldErrors.embedding_dimensions =
          "Dimensions cannot be changed after creation.";
      }
      if (Number.isFinite(embContextWindow)) {
        const v = validateNumberRange(
          embContextWindow,
          1,
          1_000_000,
          "Context window",
        );
        if (!v.valid)
          fieldErrors.embedding_contextWindow =
            v.error || "Must be between 1 and 1,000,000.";
      }
      if (Number.isFinite(embTimeout)) {
        const v = validateNumberRange(embTimeout, 1, 600, "Timeout");
        if (!v.valid)
          fieldErrors.embedding_timeoutSeconds =
            v.error || "Must be between 1 and 600.";
      }
      if (Number.isFinite(embRetries)) {
        const v = validateNumberRange(embRetries, 0, 10, "Max retries");
        if (!v.valid)
          fieldErrors.embedding_maxRetries =
            v.error || "Must be between 0 and 10.";
      }
    }

    // Shared: Credentials
    if (validateCreds) {
      for (const f of credFields) {
        const val = formValues[f.name]?.trim() ?? "";
        if (f.required && !val) {
          fieldErrors[f.name] = `${f.label} is required.`;
        }
        if (f.type === "URL" && val) {
          const urlVal = validateURL(val);
          if (!urlVal.valid) {
            fieldErrors[f.name] =
              "Must be a valid URL (e.g. https://example.com/).";
          }
        }
        if (f.type === "JSON" && val) {
          const jsonVal = validateJSON(val);
          if (!jsonVal.valid) {
            fieldErrors[f.name] = jsonVal.error || "Must be valid JSON.";
          }
        }
      }
    }

    if (Object.keys(fieldErrors).length > 0) {
      setFormFieldErrors(fieldErrors);
      return;
    }

    // Build the `credentials` object with JSON fields parsed to objects (not strings).
    // The `type` field is the discriminator the BE uses per sub-config to
    // validate the shape (matches the sub-config's provider).
    const buildCredentials = (type: ProviderType): Record<string, any> => {
      const out: Record<string, any> = { type };
      for (const f of credFields) {
        const v = formValues[f.name]?.trim();
        if (!v) continue;
        if (f.type === "JSON") {
          try {
            out[f.name] = JSON.parse(v);
          } catch {
            /* validated above; ignore here */
          }
        } else if (f.type === "NUMBER") {
          const n = Number(v);
          if (Number.isFinite(n)) out[f.name] = n;
        } else {
          out[f.name] = v;
        }
      }
      return out;
    };

    // Whether credentials are complete enough to send. On edit we may skip
    // credentials entirely if the user didn't touch them.
    const allRequiredCredsFilled = credFields
      .filter((f) => f.required)
      .every((f) => !!formValues[f.name]?.trim());
    const shouldSendCreds =
      (!editConfig || userFilledAnyCred) && allRequiredCredsFilled;

    // Build a chat sub-config block for POST. Numeric knobs are optional; only
    // include when the user has a value.
    const buildChatBlock = (): Record<string, any> => {
      const block: Record<string, any> = {
        modelType: "CHAT",
        provider: selectedProvider.provider,
        modelName: formValues.chat_modelName.trim(),
      };
      if (shouldSendCreds)
        block.credentials = buildCredentials(selectedProvider.provider);
      if (Number.isFinite(chatMaxTokens)) block.maxTokens = chatMaxTokens;
      if (Number.isFinite(chatTemperature)) block.temperature = chatTemperature;
      if (Number.isFinite(chatTopP)) block.topP = chatTopP;
      if (Number.isFinite(chatTimeout)) block.timeoutSeconds = chatTimeout;
      if (Number.isFinite(chatRetries)) block.maxRetries = chatRetries;
      return block;
    };

    // Build an embedding sub-config block for POST.
    const buildEmbeddingBlock = (): Record<string, any> => {
      const block: Record<string, any> = {
        modelType: "EMBEDDING",
        provider: selectedProvider.provider,
        modelName: formValues.embedding_modelName.trim(),
      };
      if (shouldSendCreds)
        block.credentials = buildCredentials(selectedProvider.provider);
      if (Number.isFinite(embDimensions)) block.dimensions = embDimensions;
      if (Number.isFinite(embContextWindow))
        block.contextWindow = embContextWindow;
      if (Number.isFinite(embTimeout)) block.timeoutSeconds = embTimeout;
      if (Number.isFinite(embRetries)) block.maxRetries = embRetries;
      return block;
    };

    setFormSaving(true);
    try {
      if (editConfig) {
        // PUT — patch semantics with a flat body. Sub-config fields
        // (modelType/modelName/credentials/…) live at the top level; only
        // include fields the user actually changed.
        const body: Record<string, any> = {};

        if (formValues.name !== editConfig.name)
          body.name = formValues.name.trim();

        // Chat patch
        const existingChat = editConfig.chatConfiguration;
        if (chatEnabled) {
          body.modelType = "CHAT";
          if (
            !existingChat ||
            formValues.chat_modelName?.trim() !== existingChat.modelName
          ) {
            body.modelName = formValues.chat_modelName.trim();
          }
          if (
            Number.isFinite(chatMaxTokens) &&
            chatMaxTokens !== existingChat?.maxTokens
          )
            body.maxTokens = chatMaxTokens;
          if (
            Number.isFinite(chatTemperature) &&
            chatTemperature !== existingChat?.temperature
          )
            body.temperature = chatTemperature;
          if (Number.isFinite(chatTopP) && chatTopP !== existingChat?.topP)
            body.topP = chatTopP;
          if (
            Number.isFinite(chatTimeout) &&
            chatTimeout !== existingChat?.timeoutSeconds
          )
            body.timeoutSeconds = chatTimeout;
          if (
            Number.isFinite(chatRetries) &&
            chatRetries !== existingChat?.maxRetries
          )
            body.maxRetries = chatRetries;
          // Newly-enabled Chat needs provider + credentials so BE can create it.
          if (!existingChat) {
            body.provider = selectedProvider.provider;
            if (shouldSendCreds)
              body.credentials = buildCredentials(selectedProvider.provider);
          } else if (shouldSendCreds) {
            body.credentials = buildCredentials(existingChat.provider);
          }
        }

        // Embedding patch
        const existingEmb = editConfig.embeddingConfiguration;
        if (embeddingEnabled) {
          body.modelType = "EMBEDDING";
          if (
            !existingEmb ||
            formValues.embedding_modelName?.trim() !== existingEmb.modelName
          ) {
            body.modelName = formValues.embedding_modelName.trim();
          }
          if (
            Number.isFinite(embContextWindow) &&
            embContextWindow !== existingEmb?.contextWindow
          )
            body.contextWindow = embContextWindow;
          if (
            Number.isFinite(embTimeout) &&
            embTimeout !== existingEmb?.timeoutSeconds
          )
            body.timeoutSeconds = embTimeout;
          if (
            Number.isFinite(embRetries) &&
            embRetries !== existingEmb?.maxRetries
          )
            body.maxRetries = embRetries;
          // Newly-enabled Embedding needs provider + credentials + dimensions.
          if (!existingEmb) {
            body.provider = selectedProvider.provider;
            if (Number.isFinite(embDimensions))
              body.dimensions = embDimensions;
            if (shouldSendCreds)
              body.credentials = buildCredentials(selectedProvider.provider);
          } else if (shouldSendCreds) {
            body.credentials = buildCredentials(existingEmb.provider);
          }
        }

        // The bare `modelType` discriminator alone isn't a real change — treat
        // the body as empty when nothing else was touched.
        const meaningfulKeys = Object.keys(body).filter(
          (k) => k !== "modelType",
        );
        if (meaningfulKeys.length === 0) {
          showToast("warning", "No changes to save.");
          setFormSaving(false);
          setShowForm(false);
          return;
        }

        await putAPI(`${API_URL.LLM_CONFIGS}/${editConfig.id}`, body);
        showToast("success", `"${formValues.name.trim()}" updated.`);
      } else {
        if (!formOrgId) {
          setFormError("Pick an organization first.");
          setFormSaving(false);
          return;
        }
        // POST — flat body: sub-config fields (modelType/provider/modelName/
        // credentials/…) are spread at the top level alongside orgId + name.
        // The modal is single-purpose, so exactly one of the two blocks is built.
        const subBlock = chatEnabled
          ? buildChatBlock()
          : buildEmbeddingBlock();
        const body: Record<string, any> = {
          orgId: formOrgId,
          name: formValues.name.trim(),
          ...subBlock,
        };

        await postAPI(API_URL.LLM_CONFIGS, body);
        showToast(
          "success",
          `"${body.name}" created. Activate a model to start using it.`,
        );
        setSelectedOrgId(formOrgId);
      }

      setShowForm(false);
      await refreshList();
    } catch (e: any) {
      const parsed = parseApiError(e, selectedProvider);
      setFormError(parsed.banner);
      setFormErrorDetails(parsed.details ?? null);
      if (Object.keys(parsed.fieldErrors).length > 0) {
        setFormFieldErrors(parsed.fieldErrors);
      }
    } finally {
      setFormSaving(false);
    }
  };

  // Build a SubTarget for a given set + kind. Returns null if the set has no
  // sub-config of that kind (guards against dead buttons in the UI).
  const buildSubTarget = (
    cfg: LlmConfigSummary,
    kind: SubConfigKind,
  ): SubTarget | null => {
    const sub =
      kind === "chat" ? cfg.chatConfiguration : cfg.embeddingConfiguration;
    if (!sub) return null;
    return {
      setId: cfg.id,
      setName: cfg.name,
      modelId: sub.id,
      modelName: sub.modelName,
      kind,
      isActive: !!sub.isActive,
    };
  };

  // ── Activate ──────────────────────────────────────────────────────────────
  const openActivate = (target: SubTarget) => {
    setActivateTarget(target);
    setActivateError(null);
    setActivateDetails(null);
    setShowActivate(true);
  };

  const handleActivate = async () => {
    if (!activateTarget) return;
    setActivating(true);
    setActivateError(null);
    setActivateDetails(null);
    try {
      // Activate targets the per-model id (chatConfiguration.id or
      // embeddingConfiguration.id), not the set id.
      await postAPI(
        `${API_URL.LLM_CONFIGS}/${activateTarget.modelId}/activate`,
        {},
      );
      showToast(
        "success",
        `Now using "${activateTarget.modelName}" for ${activateTarget.kind}.`,
      );
      setShowActivate(false);
      await refreshList();
    } catch (e: any) {
      const parsed = parseApiError(e);
      setActivateError(parsed.banner);
      setActivateDetails(parsed.details ?? null);
    } finally {
      setActivating(false);
    }
  };

  // ── Deactivate ────────────────────────────────────────────────────────────
  const openDeactivate = (target: SubTarget) => {
    setDeactivateTarget(target);
    setDeactivateError(null);
    setShowDeactivate(true);
  };

  const handleDeactivate = async () => {
    if (!deactivateTarget) return;
    setDeactivating(true);
    setDeactivateError(null);
    try {
      await postAPI(
        `${API_URL.LLM_CONFIGS}/${deactivateTarget.modelId}/deactivate`,
        {},
      );
      showToast(
        "success",
        `"${deactivateTarget.modelName}" (${deactivateTarget.kind}) deactivated.`,
      );
      setShowDeactivate(false);
      await refreshList();
    } catch (e: any) {
      const parsed = parseApiError(e);
      setDeactivateError(parsed.banner);
    } finally {
      setDeactivating(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const openDelete = (cfg: LlmConfigSummary) => {
    setDeleteTarget(cfg);
    setShowDelete(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteAPI(`${API_URL.LLM_CONFIGS}/${deleteTarget.id}`);
      showToast("success", `"${deleteTarget.name}" deleted.`);
      setShowDelete(false);
      await refreshList();
    } catch (e: any) {
      const parsed = parseApiError(e);
      showToast("error", parsed.banner);
    } finally {
      setDeleting(false);
    }
  };

  // ── Test connection ───────────────────────────────────────────────────────
  // Test targets a per-model id (chatConfiguration.id or embeddingConfiguration.id).
  const handleTest = async (target: SubTarget) => {
    setTestingId(target.modelId);
    try {
      const res = await postAPI(
        `${API_URL.LLM_CONFIGS}/${target.modelId}/test`,
        {},
      );
      const data = res?.data;
      if (data?.ok) {
        showToast("success", `Connection OK (${data.latencyMs ?? "?"} ms).`);
      } else {
        const code = data?.errorCode ? ` [${data.errorCode}]` : "";
        showToast(
          "error",
          (data?.errorMessage ??
            "Connection failed. Check your configuration.") + code,
        );
      }
    } catch (e: any) {
      const parsed = parseApiError(e);
      showToast("error", parsed.banner);
    } finally {
      setTestingId(null);
    }
  };

  // ── Chat tester ───────────────────────────────────────────────────────────
  const openChat = () => {
    setChatPrompt("");
    setChatResponse(null);
    setChatError(null);
    setChatErrorDetails(null);
    setShowChat(true);
  };

  const handleSendChat = async () => {
    const prompt = chatPrompt.trim();
    if (!prompt || chatSending || !selectedOrgId) return;
    setChatSending(true);
    setChatError(null);
    setChatErrorDetails(null);
    setChatResponse(null);
    try {
      const res = await postAPI(API_URL.LLM_CHAT, {
        orgId: selectedOrgId,
        prompt,
      });
      setChatResponse(res?.data ?? null);
    } catch (e: any) {
      const parsed = parseApiError(e);
      setChatError(parsed.banner);
      setChatErrorDetails(parsed.details ?? null);
    } finally {
      setChatSending(false);
    }
  };

  /* ── Search submit ──
   * Typing alone doesn't re-filter: the keyword is committed on Enter / the
   * search button, or the moment the box is cleared — matching every other
   * list in the app. This grid shows every configuration for the selected
   * org (no pagination, no sorting), so the filter itself stays local. */
  const [llmKeyword, setLlmKeyword] = useState("");
  const submitLlmSearch = (value: string) => {
    const next = value.trim();
    if (next === llmKeyword) return;
    setLlmKeyword(next);
  };

  const filteredConfigs = useMemo(() => {
    const q = llmKeyword.trim().toLowerCase();
    if (!q) return configs;
    // `provider` and `modelName` live on the chat / embedding sub-configs, not
    // on the set itself, and either sub-config can be null — reading them off
    // the set gave `undefined.toLowerCase()` and blew up the page on search.
    // The provider's display name ("Azure OpenAI") is matched too, since that
    // is the label the card actually shows, not the raw AZURE_OPENAI enum.
    return configs.filter((c) => {
      const provider =
        c.chatConfiguration?.provider ?? c.embeddingConfiguration?.provider;
      return [
        c.name,
        provider,
        providers.find((p) => p.provider === provider)?.displayName,
        c.chatConfiguration?.modelName,
        c.embeddingConfiguration?.modelName,
      ]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(q));
    });
  }, [configs, llmKeyword, providers]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const credFields =
    selectedProvider?.fields.filter((f) => f.name !== "modelName") ?? [];
  const modelField = selectedProvider?.fields.find(
    (f) => f.name === "modelName",
  );
  const modelExamples = modelField?.examples ?? [];
  const embeddingModelExamples = selectedProvider
    ? (EMBEDDING_MODEL_EXAMPLES[selectedProvider.provider] ?? [])
    : [];
  const embeddingModelPlaceholder = selectedProvider
    ? (EMBEDDING_MODEL_PLACEHOLDER[selectedProvider.provider] ??
      "e.g. bge-small")
    : "e.g. bge-small";

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1>LLM Configuration</h1>
          <p className="text-sm d mt-0.5">
            Configure the LLM provider used for AI features in each
            organization. One active LLM per org.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={openChat}
            disabled={loading || !activeConfig || !selectedOrgId}
            title={
              !selectedOrgId
                ? "Pick an organization first"
                : activeConfig
                  ? "Send a test prompt to the active LLM"
                  : "Activate an LLM first"
            }
            className="h-9 flex shrink-0 whitespace-nowrap items-center gap-2 px-4 rounded-xl border border-[#e2e8f0] dark:border-white/[0.08] bg-white/60 dark:bg-white/[0.03] text-sm font-semibold text-[#475569] dark:text-white/70 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.06] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="size-4" />
            Send Test Prompt
          </button>
          <RBACAction module="llm_configuration" action="create">
            <button
              onClick={openAdd}
              disabled={
                loading ||
                providers.length === 0 ||
                orgsLoading ||
                orgs.length === 0
              }
              className="h-9 flex shrink-0 whitespace-nowrap items-center gap-2 px-4 rounded-xl bg-indigo-600 dark:bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 dark:hover:bg-indigo-700 transition-all shadow-[0_4px_14px_rgba(79,70,229,0.3)] dark:shadow-[0_4px_14px_rgba(139,92,246,0.3)] hover:-translate-y-px disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
            >
              <Plus className="size-4" />
              Add LLM
            </button>
          </RBACAction>
        </div>
      </div>

      {/* Org-fetch error */}
      {orgsError && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-red-700 dark:text-red-400">
          <AlertTriangle className="size-4 flex-shrink-0" />
          <p className="text-sm flex-1">{orgsError}</p>
        </div>
      )}

      {/* No-active banner — only when list has rows but none active */}
      {!loading &&
        !error &&
        selectedOrgId &&
        configs.length > 0 &&
        !activeConfig && (
          <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-700 dark:text-amber-400">
            <AlertTriangle className="size-4 flex-shrink-0" />
            <p className="text-sm">
              <span className="font-medium">
                No active LLM for {selectedOrg?.name ?? "this organization"}
              </span>{" "}
              — AI features are disabled. Activate one below.
            </p>
          </div>
        )}

      {/* Search bar + organization selector — rendered outside the state
          branches below so the Organization selector keeps its position and the
          current selection whether the org has configurations, is empty, or
          failed to load. */}
      <div className="flex flex-wrap items-center gap-3">
        <TableToolbar
          className="flex-none w-full max-w-sm"
          search={{
            value: llmSearch,
            onChange: setLlmSearch,
            onSubmit: submitLlmSearch,
            placeholder: "Search configurations",
            ariaLabel: "Search LLM configurations",
          }}
        />
        {/* Organization selector — placed immediately after the Search bar.
            Scopes every LLM Configuration action to the chosen org (shared
            with the AI Module Configuration). Uses the same pill dropdown as
            the Users screen; the first option is pre-selected. */}
        <div className="flex-shrink-0">
          <Dropdown
            variant="pill"
            className="min-w-[180px]"
            value={selectedOrgId}
            onChange={(v) => setSelectedOrgId(v)}
            loading={orgsLoading}
            options={orgs.map((o) => ({ value: o.id, label: o.name }))}
          />
        </div>
      </div>

      {/* Pick-an-org prompt — shown before an org is picked */}
      {!orgsLoading && !orgsError && !selectedOrgId ? (
        <div className="w-full rounded-lg border border-border bg-card py-16 flex flex-col items-center gap-4">
          <div className="size-14 rounded-full flex items-center justify-center bg-[#f1f5f9] dark:bg-white/[0.05]">
            <Cloud className="size-6 text-[#64748b] dark:text-white/40" />
          </div>
          <div className="text-center space-y-1">
            <p className="text-sm font-medium text-[#475569] dark:text-white/60">
              Pick an organization to manage its LLMs
            </p>
            <p className="text-xs text-[#94a3b8] dark:text-white/30">
              LLM configurations are scoped per organization.
            </p>
          </div>
        </div>
      ) : (
        <>
          {/* Chat vs Embedding tabs — scopes the list fetch. */}
          <div className="flex items-center gap-1 border-b border-[#e2e8f0] dark:border-white/[0.07]">
            {(
              [
                { key: "CHAT" as const, label: "Chat" },
                { key: "EMBEDDING" as const, label: "Embedding" },
              ]
            ).map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => {
                  if (listModelType === t.key) return;
                  setListModelType(t.key);
                  setLlmSearch("");
                }}
                className={`relative px-4 py-2 text-sm font-medium transition-colors -mb-px border-b-2 ${
                  listModelType === t.key
                    ? "border-indigo-600 text-indigo-600 dark:text-indigo-400"
                    : "border-transparent text-[#64748b] dark:text-white/50 hover:text-[#0f172a] dark:hover:text-white"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          {/* Card grid */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="size-6 animate-spin d/40" />
            </div>
          ) : error ? (
            <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-red-700 dark:text-red-400">
              <AlertTriangle className="size-4 flex-shrink-0" />
              <p className="text-sm flex-1">{error}</p>
              <button onClick={fetchData} className="text-sm underline">
                Retry
              </button>
            </div>
          ) : filteredConfigs.length === 0 ? (
            llmSearch.trim() ? (
              <p className="text-sm d py-10 text-center">
                No configurations match "{llmSearch}"
              </p>
            ) : (
              <div className="w-full rounded-lg border border-border bg-card py-16 flex flex-col items-center gap-4">
                <div className="size-14 rounded-full flex items-center justify-center bg-[#f1f5f9] dark:bg-white/[0.05]">
                  <Cloud className="size-6 text-[#64748b] dark:text-white/40" />
                </div>
                <div className="text-center space-y-1">
                  <p className="text-sm font-medium text-[#475569] dark:text-white/60">
                    No{" "}
                    {listModelType === "CHAT" ? "chat" : "embedding"} LLM
                    configured for{" "}
                    {selectedOrg?.name ?? "this organization"}.
                  </p>
                  <p className="text-xs text-[#94a3b8] dark:text-white/30">
                    Connect a provider like Groq, AWS Bedrock or a Local LLM to
                    enable AI features.
                  </p>
                </div>
                <button
                  onClick={openAdd}
                  disabled={providers.length === 0}
                  className="h-9 flex shrink-0 whitespace-nowrap items-center gap-2 px-4 rounded-xl bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 transition-all shadow-[0_4px_14px_rgba(79,70,229,0.3)] hover:-translate-y-px disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                >
                  <Plus className="size-4" />
                  Add LLM
                </button>
              </div>
            )
          ) : (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredConfigs.map((cfg) => {
                  const chat = cfg.chatConfiguration;
                  const emb = cfg.embeddingConfiguration;
                  // Show the chat model as the primary; fall back to embedding
                  // so an embedding-only config still renders a full card.
                  const primary = chat ?? emb;
                  const primaryKind: SubConfigKind = chat ? "chat" : "embedding";
                  const headerProvider = primary?.provider;
                  const meta = headerProvider
                    ? providers.find((p) => p.provider === headerProvider)
                    : undefined;
                  const anyActive = !!chat?.isActive || !!emb?.isActive;
                  const anySubActive = anyActive;
                  // Params for the summary table. Chat vs. embedding surface
                  // slightly different fields.
                  const paramRows: { label: string; value: string }[] = [];
                  if (primary && primaryKind === "chat") {
                    const c = primary as ChatSubConfig;
                    if (c.maxTokens != null)
                      paramRows.push({ label: "Max Tokens", value: String(c.maxTokens) });
                    if (c.temperature != null)
                      paramRows.push({ label: "Temperature", value: c.temperature.toFixed(1) });
                    if (c.topP != null)
                      paramRows.push({ label: "Top P", value: c.topP.toFixed(2) });
                  } else if (primary && primaryKind === "embedding") {
                    const e = primary as EmbeddingSubConfig;
                    if (e.dimensions != null)
                      paramRows.push({ label: "Dimensions", value: String(e.dimensions) });
                    if (e.contextWindow != null)
                      paramRows.push({ label: "Context Window", value: String(e.contextWindow) });
                  }
                  if (primary?.timeoutSeconds != null)
                    paramRows.push({ label: "Timeout", value: `${primary.timeoutSeconds}s` });
                  if (primary?.maxRetries != null)
                    paramRows.push({ label: "Max Retries", value: String(primary.maxRetries) });
                  return (
                    <div
                      key={cfg.id}
                      className={`rounded-xl border bg-card overflow-hidden flex flex-col transition-shadow ${
                        anyActive
                          ? "border-emerald-500/40 shadow-[0_0_0_1px_rgba(16,185,129,0.15),0_4px_24px_rgba(16,185,129,0.12)]"
                          : "border-border shadow-sm"
                        }`}
                    >
                      {/* Card header — provider + status */}
                      <div className="flex items-center justify-between gap-3 px-5 pt-5 pb-4">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="size-10 shrink-0 rounded-xl flex items-center justify-center bg-[#f1f5f9] dark:bg-white/[0.06]">
                            {headerProvider ? (
                              <ProviderIcon provider={headerProvider} size={22} />
                            ) : (
                              <Bot size={22} className="text-[#64748b]" />
                            )}
                          </div>
                          <p className="font-bold text-[#0f172a] dark:text-white text-[15px] truncate">
                            {meta?.displayName ?? headerProvider ?? "—"}
                          </p>
                        </div>
                        {anyActive ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400 shrink-0">
                            <span className="size-1.5 rounded-full bg-emerald-500" />
                            Active
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[#f1f5f9] text-[#64748b] dark:bg-white/[0.06] dark:text-white/50 shrink-0">
                            <span className="size-1.5 rounded-full bg-[#94a3b8]" />
                            Inactive
                          </span>
                        )}
                      </div>

                      {/* Card body */}
                      <div className="px-5 pb-4 flex-1 space-y-3">
                        {/* Config name + model */}
                        <div className="space-y-1.5">
                          <p className="font-bold text-[#0f172a] dark:text-white text-[15px] break-words">
                            {cfg.name}
                          </p>
                          {primary ? (
                            <span className="inline-block font-mono text-xs bg-[#f1f5f9] dark:bg-white/[0.06] px-2 py-0.5 rounded text-[#0f172a] dark:text-white max-w-full break-all">
                              {primary.modelName}
                            </span>
                          ) : (
                            <span className="text-[11px] text-[#94a3b8] dark:text-white/40">
                              No model configured
                            </span>
                          )}
                        </div>

                        {/* Parameter list */}
                        {paramRows.length > 0 && (
                          <div className="rounded-lg border border-[#e2e8f0] dark:border-white/[0.06] divide-y divide-[#e2e8f0] dark:divide-white/[0.06] overflow-hidden">
                            {paramRows.map((row) => (
                              <div
                                key={row.label}
                                className="flex items-center justify-between px-3 py-2 bg-[#f8fafc] dark:bg-white/[0.02]"
                              >
                                <span className="text-[12px] text-[#475569] dark:text-white/70">
                                  {row.label}
                                </span>
                                <span className="text-[12px] font-semibold text-[#0f172a] dark:text-white tabular-nums">
                                  {row.value}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}

                        <p className="text-xs text-[#0f172a] dark:text-white/85">
                          Created {formatDate(cfg.createdAt)}
                          {cfg.updatedAt && cfg.updatedAt !== cfg.createdAt && (
                            <span className="ml-2 text-[#0f172a] dark:text-white/85">
                              · Updated {formatDate(cfg.updatedAt)}
                            </span>
                          )}
                        </p>
                      </div>

                      {/* Card footer — actions */}
                      <div className="border-t border-border px-4 py-2.5 flex flex-wrap items-center gap-2 bg-[#fafafa] dark:bg-white/[0.02]">
                        {primary && (
                          <Tip label="Test connection">
                            <button
                              onClick={() => {
                                const t = buildSubTarget(cfg, primaryKind);
                                if (t) handleTest(t);
                              }}
                              disabled={testingId === primary.id}
                              className="flex shrink-0 items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-violet-200 bg-violet-50 text-violet-600 dark:border-violet-500/25 dark:bg-violet-500/10 dark:text-violet-400 hover:bg-violet-100 dark:hover:bg-violet-500/20 transition-colors disabled:opacity-40"
                            >
                              {testingId === primary.id ? (
                                <Loader2 className="size-3.5 animate-spin" />
                              ) : (
                                <Activity className="size-3.5" />
                              )}
                              <span className="text-[11px] font-semibold">Test</span>
                            </button>
                          </Tip>
                        )}
                        {primary && (
                          <Tip label={primary.isActive ? "Already active" : "Activate"}>
                            <button
                              onClick={() => {
                                const t = buildSubTarget(cfg, primaryKind);
                                if (t) openActivate(t);
                              }}
                              disabled={primary.isActive}
                              className="flex shrink-0 items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/25 dark:bg-emerald-500/10 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-500/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              <Power className="size-3.5" />
                              <span className="text-[11px] font-semibold">Activate</span>
                            </button>
                          </Tip>
                        )}
                        {primary && (
                          <Tip label={primary.isActive ? "Deactivate" : "Not active"}>
                            <button
                              onClick={() => {
                                const t = buildSubTarget(cfg, primaryKind);
                                if (t) openDeactivate(t);
                              }}
                              disabled={!primary.isActive}
                              className="flex shrink-0 items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/25 dark:bg-amber-500/10 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-500/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              <Ban className="size-3.5" />
                              <span className="text-[11px] font-semibold">Deactivate</span>
                            </button>
                          </Tip>
                        )}
                        <RBACAction module="llm_configuration" action="update">
                          <Tip label="Edit Configuration">
                            <button
                              onClick={() => openEdit(cfg)}
                              disabled={editLoadingId === cfg.id}
                              className="flex shrink-0 items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-indigo-200 bg-indigo-50 text-indigo-600 dark:border-indigo-500/25 dark:bg-indigo-500/10 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-500/20 transition-colors disabled:opacity-40"
                            >
                              {editLoadingId === cfg.id ? (
                                <Loader2 className="size-3.5 animate-spin" />
                              ) : (
                                <Pencil className="size-3.5" />
                              )}
                              <span className="text-[11px] font-semibold">Edit</span>
                            </button>
                          </Tip>
                        </RBACAction>

                        <RBACAction module="llm_configuration" action="delete">
                          <Tip
                            label={
                              anySubActive
                                ? "Deactivate first to delete."
                                : "Delete Configuration"
                            }
                          >
                            <button
                              onClick={() => openDelete(cfg)}
                              disabled={anySubActive}
                              className="flex shrink-0 items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-red-200 bg-red-50 text-red-600 dark:border-red-500/25 dark:bg-red-500/10 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-500/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              <Trash2 className="size-3.5" />
                              <span className="text-[11px] font-semibold">Delete</span>
                            </button>
                          </Tip>
                        </RBACAction>
                      </div>
                    </div>
                  );
                })}
              </div>

              <p className="text-xs d">
                Showing <strong>1–{filteredConfigs.length}</strong> of{" "}
                <strong>{configs.length}</strong> configurations
              </p>
            </>
          )}
        </>
      )}

      {/* ──────────────────────────────────────────────────────────────────────
          Form Modal  (Add / Edit)
      ────────────────────────────────────────────────────────────────────── */}
      <Modal
        open={showForm}
        onClose={() => !formSaving && setShowForm(false)}
        title={
          editConfig
            ? `Edit — ${editConfig.name}`
            : `Add ${formModelType === "CHAT" ? "Chat" : "Embedding"} LLM Configuration`
        }
        description={
          editConfig
            ? "Update the connection details for this provider."
            : "Configure the connection details for this provider."
        }
        size="lg"
        wide
        closable={!formSaving}
        footer={
          <ModalFooterButtons
            onCancel={() => setShowForm(false)}
            onSubmit={handleSave}
            submitLabel={formSaving ? "Saving…" : "Save"}
            submitDisabled={formSaving || !selectedProvider}
          />
        }
      >
        <div className="space-y-4">
          {/* Provider + Organization on the same row (create only). On edit, the read-only badge below shows the provider. */}
          {!editConfig && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                label="Provider"
                required
                hint="LLM provider for this configuration."
              >
                <FormSelect
                  value={selectedProvider?.provider ?? ""}
                  onChange={(v) => handleProviderChange(v)}
                  options={[
                    { value: "", label: "Select a provider" },
                    ...providers.map((p) => ({
                      value: p.provider,
                      label: p.displayName,
                    })),
                  ]}
                />
              </FormField>

              {selectedProvider && (
                <FormField
                  label="Organization"
                  required
                  hint="The organization this LLM will be configured for."
                >
                  <FormSelect
                    value={formOrgId}
                    onChange={(v) => setFormOrgId(v)}
                    options={
                      orgsLoading
                        ? [{ value: "", label: "Loading organizations…" }]
                        : orgs.length === 0
                          ? [{ value: "", label: "No organizations available" }]
                          : orgs.map((o) => ({ value: o.id, label: o.name }))
                    }
                  />
                </FormField>
              )}
            </div>
          )}

          {/* Fields shown only after a provider is selected */}
          {selectedProvider && (
            <div className="space-y-4">
              {/* Top-level error banner */}
              {formError && (
                <div className="rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 overflow-hidden">
                  <div className="flex items-start gap-3 p-3 text-red-700 dark:text-red-400">
                    <AlertTriangle className="size-4 flex-shrink-0 mt-0.5" />
                    <p className="text-sm">{formError}</p>
                  </div>
                  {formErrorDetails && (
                    <div className="px-3 pb-3 pt-0 space-y-1">
                      <p className="text-[11px] font-semibold tracking-wide text-red-700/70 dark:text-red-400/70">
                        Provider response
                      </p>
                      <pre className="text-xs p-2.5 rounded-md bg-white/60 dark:bg-black/20 whitespace-pre-wrap break-words border border-red-200/60 dark:border-red-500/20 leading-relaxed text-[#475569] dark:text-white/70 font-mono">
                        {formErrorDetails}
                      </pre>
                    </div>
                  )}
                </div>
              )}

              {/* Provider badge */}
              <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg bg-[#f8fafc] dark:bg-white/[0.03] border border-[#e2e8f0] dark:border-white/[0.06]">
                <ProviderIcon provider={selectedProvider.provider} size={16} />
                <span className="text-sm font-medium text-[#0f172a] dark:text-white">
                  {selectedProvider.displayName}
                </span>
                <span className="ml-auto text-xs text-[#94a3b8] dark:text-white/30">
                  {editConfig ? "Provider (read-only)" : "Selected provider"}
                </span>
              </div>

              {/* Name */}
              <FormField
                label="Name"
                required
                hint="A friendly display name for this configuration."
              >
                <FormInput
                  value={formValues.name ?? ""}
                  onChange={(v) => setField("name", v)}
                  placeholder="e.g. Production OpenAI"
                  name="llm-config-name"
                  autoComplete="off"
                />
                {formFieldErrors.name && (
                  <p className="text-xs text-red-500 mt-0.5">
                    {formFieldErrors.name}
                  </p>
                )}
              </FormField>

              {/* Provider-specific credential fields (shared across both tabs). JSON fields
                  span the full width; everything else pairs up two-per-row. */}
              {(() => {
                const nodes: React.ReactNode[] = [];
                let pair: typeof credFields = [];
                const flushPair = () => {
                  if (pair.length === 0) return;
                  nodes.push(
                    <div
                      key={`cred-pair-${nodes.length}`}
                      className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                    >
                      {pair.map((f) => (
                        <ProviderFieldInput
                          key={f.name}
                          field={f}
                          value={formValues[f.name] ?? ""}
                          onChange={(v) => setField(f.name, v)}
                          showPassword={showPw[f.name] ?? false}
                          onTogglePassword={() =>
                            setShowPw((prev) => ({
                              ...prev,
                              [f.name]: !prev[f.name],
                            }))
                          }
                          error={formFieldErrors[f.name]}
                          isEdit={!!editConfig}
                        />
                      ))}
                    </div>,
                  );
                  pair = [];
                };
                for (const f of credFields) {
                  if (f.type === "JSON") {
                    flushPair();
                    nodes.push(
                      <ProviderFieldInput
                        key={f.name}
                        field={f}
                        value={formValues[f.name] ?? ""}
                        onChange={(v) => setField(f.name, v)}
                        showPassword={showPw[f.name] ?? false}
                        onTogglePassword={() =>
                          setShowPw((prev) => ({
                            ...prev,
                            [f.name]: !prev[f.name],
                          }))
                        }
                        error={formFieldErrors[f.name]}
                        isEdit={!!editConfig}
                      />,
                    );
                  } else {
                    pair.push(f);
                    if (pair.length === 2) flushPair();
                  }
                }
                flushPair();
                return nodes;
              })()}

              {/* Chat-only section (rendered when the modal is scoped to CHAT) */}
              {formModelType === "CHAT" && (
                <div className="space-y-4">
                  <FormField label="Chat Model Name" required>
                        <div className="space-y-1.5">
                          <FormInput
                            value={formValues.chat_modelName ?? ""}
                            onChange={(v) => setField("chat_modelName", v)}
                            placeholder={modelField?.placeholder ?? "e.g. gpt-4o"}
                            name="llm-chat-model-id"
                            autoComplete="off"
                          />
                          {modelField?.helpText && (
                            <p className="text-xs d">{modelField.helpText}</p>
                          )}
                          {modelExamples.length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                              {modelExamples.map((ex) => (
                                <button
                                  key={ex}
                                  type="button"
                                  onClick={() => setField("chat_modelName", ex)}
                                  className="text-xs px-2 py-0.5 rounded-md bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-500/20 transition-colors font-mono"
                                >
                                  {ex}
                                </button>
                              ))}
                            </div>
                          )}
                          {formFieldErrors.chat_modelName && (
                            <p className="text-xs text-red-500">
                              {formFieldErrors.chat_modelName}
                            </p>
                          )}
                        </div>
                      </FormField>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <FormField label="Max Tokens" hint="1 — 1,000,000">
                          <FormInput
                            type="number"
                            value={formValues.chat_maxTokens ?? "4096"}
                            onChange={(v) => setField("chat_maxTokens", v)}
                            placeholder="4096"
                            name="llm-chat-max-tokens"
                            autoComplete="off"
                          />
                          {formFieldErrors.chat_maxTokens && (
                            <p className="text-xs text-red-500 mt-0.5">
                              {formFieldErrors.chat_maxTokens}
                            </p>
                          )}
                        </FormField>

                        <FormField label="Max Retries" hint="0 — 10">
                          <FormInput
                            type="number"
                            value={formValues.chat_maxRetries ?? "3"}
                            onChange={(v) => setField("chat_maxRetries", v)}
                            placeholder="3"
                            name="llm-chat-max-retries"
                            autoComplete="off"
                          />
                          {formFieldErrors.chat_maxRetries && (
                            <p className="text-xs text-red-500 mt-0.5">
                              {formFieldErrors.chat_maxRetries}
                            </p>
                          )}
                        </FormField>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <FormField
                          label={`Temperature — ${parseFloat(formValues.chat_temperature ?? "0.7").toFixed(1)}`}
                          hint="Controls randomness. Lower = more deterministic, higher = more creative."
                        >
                          <input
                            type="range"
                            min={0}
                            max={2}
                            step={0.1}
                            value={formValues.chat_temperature ?? "0.7"}
                            onChange={(e) => setField("chat_temperature", e.target.value)}
                            className="w-full h-2 bg-[#e2e8f0] dark:bg-white/[0.07] rounded-lg appearance-none cursor-pointer accent-indigo-600"
                          />
                          <div className="flex justify-between text-xs text-[#94a3b8] dark:text-white/30 mt-1 px-0.5">
                            <span>0</span>
                            <span>1</span>
                            <span>2</span>
                          </div>
                          {formFieldErrors.chat_temperature && (
                            <p className="text-xs text-red-500 mt-0.5">
                              {formFieldErrors.chat_temperature}
                            </p>
                          )}
                        </FormField>

                        <FormField
                          label={`Top P — ${parseFloat(formValues.chat_topP ?? "1.0").toFixed(2)}`}
                          hint="Nucleus sampling threshold."
                        >
                          <input
                            type="range"
                            min={0}
                            max={1}
                            step={0.01}
                            value={formValues.chat_topP ?? "1.0"}
                            onChange={(e) => setField("chat_topP", e.target.value)}
                            className="w-full h-2 bg-[#e2e8f0] dark:bg-white/[0.07] rounded-lg appearance-none cursor-pointer accent-indigo-600"
                          />
                          <div className="flex justify-between text-xs text-[#94a3b8] dark:text-white/30 mt-1 px-0.5">
                            <span>0</span>
                            <span>1</span>
                          </div>
                          {formFieldErrors.chat_topP && (
                            <p className="text-xs text-red-500 mt-0.5">
                              {formFieldErrors.chat_topP}
                            </p>
                          )}
                        </FormField>
                      </div>

                      <FormField label="Timeout (seconds)" hint="1 — 600">
                        <FormInput
                          type="number"
                          value={formValues.chat_timeoutSeconds ?? "60"}
                          onChange={(v) => setField("chat_timeoutSeconds", v)}
                          placeholder="60"
                          name="llm-chat-timeout"
                          autoComplete="off"
                        />
                        {formFieldErrors.chat_timeoutSeconds && (
                          <p className="text-xs text-red-500 mt-0.5">
                            {formFieldErrors.chat_timeoutSeconds}
                          </p>
                        )}
                      </FormField>
                </div>
              )}

              {/* Embedding-only section (rendered when the modal is scoped to EMBEDDING) */}
              {formModelType === "EMBEDDING" && (
                <div className="space-y-4">
                  <FormField label="Embedding Model Name" required>
                        <div className="space-y-1.5">
                          <FormInput
                            value={formValues.embedding_modelName ?? ""}
                            onChange={(v) => setField("embedding_modelName", v)}
                            placeholder={embeddingModelPlaceholder}
                            name="llm-embedding-model-id"
                            autoComplete="off"
                          />
                          {modelField?.helpText && (
                            <p className="text-xs d">{modelField.helpText}</p>
                          )}
                          {embeddingModelExamples.length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                              {embeddingModelExamples.map((ex) => (
                                <button
                                  key={ex}
                                  type="button"
                                  onClick={() =>
                                    setField("embedding_modelName", ex)
                                  }
                                  className="text-xs px-2 py-0.5 rounded-md bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-500/20 transition-colors font-mono"
                                >
                                  {ex}
                                </button>
                              ))}
                            </div>
                          )}
                          {formFieldErrors.embedding_modelName && (
                            <p className="text-xs text-red-500">
                              {formFieldErrors.embedding_modelName}
                            </p>
                          )}
                        </div>
                      </FormField>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <FormField
                          label="Dimensions"
                          required
                          hint={
                            editConfig?.embeddingConfiguration
                              ? "Immutable after creation."
                              : "Vector size for this embedding model (positive integer)."
                          }
                        >
                          <FormInput
                            type="number"
                            value={formValues.embedding_dimensions ?? ""}
                            onChange={(v) => setField("embedding_dimensions", v)}
                            placeholder="e.g. 384"
                            name="llm-embedding-dimensions"
                            autoComplete="off"
                            disabled={!!editConfig?.embeddingConfiguration}
                          />
                          {formFieldErrors.embedding_dimensions && (
                            <p className="text-xs text-red-500 mt-0.5">
                              {formFieldErrors.embedding_dimensions}
                            </p>
                          )}
                        </FormField>

                        <FormField
                          label="Context Window"
                          hint="Max input tokens for this embedding model (optional)."
                        >
                          <FormInput
                            type="number"
                            value={formValues.embedding_contextWindow ?? ""}
                            onChange={(v) =>
                              setField("embedding_contextWindow", v)
                            }
                            placeholder="e.g. 512"
                            name="llm-embedding-context-window"
                            autoComplete="off"
                          />
                          {formFieldErrors.embedding_contextWindow && (
                            <p className="text-xs text-red-500 mt-0.5">
                              {formFieldErrors.embedding_contextWindow}
                            </p>
                          )}
                        </FormField>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <FormField label="Timeout (seconds)" hint="1 — 600">
                          <FormInput
                            type="number"
                            value={formValues.embedding_timeoutSeconds ?? "60"}
                            onChange={(v) =>
                              setField("embedding_timeoutSeconds", v)
                            }
                            placeholder="60"
                            name="llm-embedding-timeout"
                            autoComplete="off"
                          />
                          {formFieldErrors.embedding_timeoutSeconds && (
                            <p className="text-xs text-red-500 mt-0.5">
                              {formFieldErrors.embedding_timeoutSeconds}
                            </p>
                          )}
                        </FormField>

                        <FormField label="Max Retries" hint="0 — 10">
                          <FormInput
                            type="number"
                            value={formValues.embedding_maxRetries ?? "3"}
                            onChange={(v) => setField("embedding_maxRetries", v)}
                            placeholder="3"
                            name="llm-embedding-max-retries"
                            autoComplete="off"
                          />
                          {formFieldErrors.embedding_maxRetries && (
                            <p className="text-xs text-red-500 mt-0.5">
                              {formFieldErrors.embedding_maxRetries}
                            </p>
                          )}
                        </FormField>
                      </div>
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>

      {/* ──────────────────────────────────────────────────────────────────────
          Activate Confirmation Modal
      ────────────────────────────────────────────────────────────────────── */}
      <Modal
        open={showActivate}
        onClose={() => !activating && setShowActivate(false)}
        title={
          activateTarget
            ? `Activate ${activateTarget.kind === "chat" ? "Chat" : "Embedding"} model "${activateTarget.modelName}"?`
            : "Activate model?"
        }
        size="sm"
        closable={!activating}
        footer={
          <ModalFooterButtons
            onCancel={() => setShowActivate(false)}
            onSubmit={handleActivate}
            submitLabel={activating ? "Activating" : "Activate"}
            submitDisabled={activating}
          />
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-[#475569] dark:text-white/60">
            {activateTarget?.kind === "chat" ? "Chat" : "Embedding"} features
            will start using{" "}
            <span className="font-medium text-[#0f172a] dark:text-white">
              "{activateTarget?.modelName}"
            </span>{" "}
            immediately. Any previously active{" "}
            {activateTarget?.kind === "chat" ? "chat" : "embedding"} model in
            this organization will be deactivated.
          </p>

          {activating && (
            <div className="flex items-center gap-2 text-sm text-[#64748b] dark:text-white/50">
              <Loader2 className="size-4 animate-spin" />
              Verifying connection with provider
            </div>
          )}

          {activateError && (
            <div className="rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 overflow-hidden">
              <div className="flex items-start gap-2 p-3 text-red-700 dark:text-red-400 text-sm">
                <AlertTriangle className="size-4 flex-shrink-0 mt-0.5" />
                <p>{activateError}</p>
              </div>
              {activateDetails && (
                <div className="px-3 pb-3 pt-0 space-y-1">
                  <p className="text-[11px] font-semibold tracking-wide text-red-700/70 dark:text-red-400/70">
                    Provider response
                  </p>
                  <pre className="text-xs p-2.5 rounded-md bg-white/60 dark:bg-black/20 whitespace-pre-wrap break-words border border-red-200/60 dark:border-red-500/20 leading-relaxed text-[#475569] dark:text-white/70 font-mono">
                    {activateDetails}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>
      </Modal>

      {/* ──────────────────────────────────────────────────────────────────────
          Deactivate Confirmation Modal
      ────────────────────────────────────────────────────────────────────── */}
      <Modal
        open={showDeactivate}
        onClose={() => !deactivating && setShowDeactivate(false)}
        title={
          deactivateTarget
            ? `Deactivate ${deactivateTarget.kind === "chat" ? "Chat" : "Embedding"} model "${deactivateTarget.modelName}"?`
            : "Deactivate model?"
        }
        size="sm"
        closable={!deactivating}
        footer={
          <ModalFooterButtons
            onCancel={() => setShowDeactivate(false)}
            onSubmit={handleDeactivate}
            submitLabel={deactivating ? "Deactivating" : "Deactivate"}
            submitDisabled={deactivating}
          />
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-[#475569] dark:text-white/60">
            This will deactivate{" "}
            <span className="font-medium text-[#0f172a] dark:text-white">
              "{deactivateTarget?.modelName}"
            </span>
            .{" "}
            {deactivateTarget?.kind === "chat" ? "Chat" : "Embedding"} features
            will be disabled for this organization until another{" "}
            {deactivateTarget?.kind === "chat" ? "chat" : "embedding"} model is
            activated.
          </p>

          {deactivating && (
            <div className="flex items-center gap-2 text-sm text-[#64748b] dark:text-white/50">
              <Loader2 className="size-4 animate-spin" />
              Deactivating
            </div>
          )}

          {deactivateError && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 text-red-700 dark:text-red-400 text-sm">
              <AlertTriangle className="size-4 flex-shrink-0 mt-0.5" />
              <p>{deactivateError}</p>
            </div>
          )}
        </div>
      </Modal>

      {/* ──────────────────────────────────────────────────────────────────────
          Delete Confirmation Modal
      ────────────────────────────────────────────────────────────────────── */}
      <Modal
        open={showDelete}
        onClose={() => !deleting && setShowDelete(false)}
        title={`Delete "${deleteTarget?.name}"?`}
        size="sm"
        closable={!deleting}
        footer={
          <div className="flex items-center justify-end gap-3 w-full">
            <button
              onClick={() => setShowDelete(false)}
              disabled={deleting}
              className="px-4 py-2 rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] text-sm font-medium text-[#475569] dark:text-white/60 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] transition-colors disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-bold hover:bg-red-700 transition-colors disabled:opacity-40 shadow-md shadow-red-500/20"
            >
              {deleting ? "Deleting…" : "Delete"}
            </button>
          </div>
        }
      >
        <p className="text-sm text-[#475569] dark:text-white/60">
          This action cannot be undone. All configuration data for{" "}
          <span className="font-medium text-[#0f172a] dark:text-white">
            "{deleteTarget?.name}"
          </span>{" "}
          will be permanently removed.
        </p>
      </Modal>

      {/* ──────────────────────────────────────────────────────────────────────
          Chat Tester Modal
      ────────────────────────────────────────────────────────────────────── */}
      <Modal
        open={showChat}
        onClose={() => !chatSending && setShowChat(false)}
        title="Send Test Prompt"
        description={
          activeConfig
            ? `Sends to ${selectedOrg?.name ? `${selectedOrg.name}'s ` : "the "}active chat LLM${activeConfig.modelName ? ` — ${activeConfig.modelName}` : ""}${activeConfig.name ? ` (${activeConfig.name})` : ""}.`
            : "No active LLM."
        }
        size="lg"
        closable={!chatSending}
        footer={
          <div className="flex w-full items-center justify-between gap-3">
            <span className="text-xs text-[#94a3b8] dark:text-white/30">
              {chatPrompt.length}/{CHAT_PROMPT_MAX}
            </span>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowChat(false)}
                disabled={chatSending}
                className="px-4 py-2 rounded-lg border border-[#e2e8f0] dark:border-white/[0.07] text-sm font-medium text-[#475569] dark:text-white/60 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04] transition-colors disabled:opacity-40"
              >
                Close
              </button>
              <button
                onClick={handleSendChat}
                disabled={
                  chatSending ||
                  !chatPrompt.trim() ||
                  chatPrompt.length > CHAT_PROMPT_MAX
                }
                className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 transition-colors disabled:opacity-40 shadow-md shadow-indigo-500/20 flex items-center gap-2"
              >
                {chatSending ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <Send className="size-3.5" />
                )}
                {chatSending ? "Sending…" : "Send"}
              </button>
            </div>
          </div>
        }
      >
        <div className="space-y-4">
          <FormField label="Prompt" required hint="Up to 5,000 characters.">
            <textarea
              value={chatPrompt}
              onChange={(e) =>
                setChatPrompt(e.target.value.slice(0, CHAT_PROMPT_MAX))
              }
              placeholder="Ask the model anything to verify it's responding…"
              rows={5}
              spellCheck={false}
              disabled={chatSending}
              className="w-full text-sm bg-[#f5f7fb] dark:bg-white/[0.04] rounded-lg px-3 py-2 outline-none border border-[#e2e8f0] dark:border-white/[0.07] focus:border-indigo-500 dark:focus:border-indigo-500 transition-all placeholder:text-[#94a3b8] dark:placeholder:text-white/25 resize-none leading-relaxed text-[#0f172a] dark:text-white disabled:opacity-50"
            />
          </FormField>

          {/* Error */}
          {chatError && (
            <div className="rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 overflow-hidden">
              <div className="flex items-start gap-2 p-3 text-red-700 dark:text-red-400 text-sm">
                <AlertTriangle className="size-4 flex-shrink-0 mt-0.5" />
                <p>{chatError}</p>
              </div>
              {chatErrorDetails && (
                <div className="px-3 pb-3 pt-0 space-y-1">
                  <p className="text-[11px] font-semibold tracking-wide text-red-700/70 dark:text-red-400/70">
                    Provider response
                  </p>
                  <pre className="text-xs p-2.5 rounded-md bg-white/60 dark:bg-black/20 whitespace-pre-wrap break-words border border-red-200/60 dark:border-red-500/20 leading-relaxed text-[#475569] dark:text-white/70 font-mono">
                    {chatErrorDetails}
                  </pre>
                </div>
              )}
            </div>
          )}

          {/* Loading */}
          {chatSending && !chatResponse && !chatError && (
            <div className="flex items-center gap-2 text-sm text-[#64748b] dark:text-white/50">
              <Loader2 className="size-4 animate-spin" />
              Waiting for response…
            </div>
          )}

          {/* Response */}
          {chatResponse && (
            <div className="space-y-2">
              <p className="text-xs font-semibold tracking-wide text-[#64748b] dark:text-white/40">
                Response
              </p>
              <pre className="text-sm p-3 rounded-lg bg-[#f8fafc] dark:bg-white/[0.03] whitespace-pre-wrap break-words border border-[#e2e8f0] dark:border-white/[0.07] leading-relaxed text-[#0f172a] dark:text-white max-h-[40vh] overflow-y-auto">
                {chatResponse.content}
              </pre>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[#64748b] dark:text-white/40">
                <span className="font-mono text-[#475569] dark:text-white/60">
                  {chatResponse.modelUsed}
                </span>
                <span>•</span>
                <span>
                  {chatResponse.inputTokens} in / {chatResponse.outputTokens}{" "}
                  out
                </span>
                <span>•</span>
                <span>{chatResponse.latencyMs} ms</span>
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
}
